import { invoke, isTauri } from '@tauri-apps/api/core'
import { listen, type UnlistenFn } from '@tauri-apps/api/event'

export interface DesktopSession {
  accessToken: string
  expiresIn: number
  user: Record<string, unknown>
}

export interface DesktopLogoutOutcome {
  localStateRemoved: boolean
  remoteSessionRevocation: 'confirmed' | 'not_needed' | 'unconfirmed'
}

export interface DesktopDeviceRevokedEvent {
  localStateRemoved: boolean
  remediationRequired: boolean
}

// Use Tauri's public startup marker. The private `__TAURI_INTERNALS__` bridge
// can attach after the first React render on a cold WebKit launch, which used
// to make Desktop render the public landing page until the user navigated.
export const isDesktopRuntime = (): boolean => isTauri()

export const loginWithDesktop = async (): Promise<DesktopSession> => (
  invoke<DesktopSession>('desktop_login')
)

let desktopRestorePromise: Promise<DesktopSession | null> | null = null

export const restoreDesktopSession = (): Promise<DesktopSession | null> => {
  // React StrictMode intentionally replays effects in development. Keep the
  // native Keychain restore single-flight so the replay cannot trip Rust's
  // credential-operation gate and expose login while the first restore is
  // still running.
  if (!desktopRestorePromise) {
    desktopRestorePromise = invoke<DesktopSession | null>('restore_desktop_session')
      .finally(() => {
        desktopRestorePromise = null
      })
  }
  return desktopRestorePromise
}

export const refreshDesktopSession = async (): Promise<DesktopSession | null> => (
  invoke<DesktopSession | null>('refresh_desktop_session')
)

export const logoutDesktop = async (): Promise<DesktopLogoutOutcome> => (
  invoke<DesktopLogoutOutcome>('desktop_logout')
)

export const onDesktopDeviceRevoked = (
  handler: (event: DesktopDeviceRevokedEvent) => void,
): Promise<UnlistenFn> => (
  listen<DesktopDeviceRevokedEvent>('desktop-device-revoked', ({ payload }) => handler(payload))
)
